import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { WorkerCard } from "@/services/api/worker-ani/types";
import { pinWorker, unpinWorker } from "@/services/api/worker-ani";
import { useAuth } from "./AuthContext";
import { useWorkersList } from "@/services/api/worker-ani/queries/useWorkersList";
import { useWorkersPinned } from "@/services/api/worker-ani/queries/useWorkersPinned";
import { useWorkersListRequested } from "@/services/api/worker-ani/queries/WorkersListGate";
import {
  patchWorkerInList,
  removeWorkerFromList,
} from "@/services/api/worker-ani/queries/patches";
import { workersKeys } from "@/services/api/worker-ani/queries/keys";

interface WorkerListContextType {
  workers: WorkerCard[];
  loading: boolean;
  error: string | null;
  pinnedWorkers: string[];
  setWorkers: (workers: WorkerCard[]) => void;
  removeWorker: (workerId: string) => void;
  addWorker: (worker: WorkerCard) => void;
  updateWorker: (workerId: string, updatedWorker: WorkerCard) => void;
  refreshWorkers: () => Promise<void>;
  toggleWorkerPin: (workerId: string) => Promise<void>;
  refreshPinnedWorkers: () => Promise<void>;
}

const WorkerListContext = createContext<WorkerListContextType | undefined>(
  undefined,
);

export function WorkerListProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const listRequested = useWorkersListRequested();

  const listEnabled = isAuthenticated && !isAuthLoading && listRequested;

  const {
    data: workers = [],
    isLoading: listLoading,
    error: listError,
  } = useWorkersList({ enabled: listEnabled, view: "summary" });

  const {
    data: pinnedCards = [],
    isLoading: pinnedLoading,
  } = useWorkersPinned({ enabled: isAuthenticated && !isAuthLoading });

  const pinnedWorkers = useMemo(
    () => pinnedCards.map((w) => w._id),
    [pinnedCards],
  );

  const refreshWorkers = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: workersKeys.list("summary") });
  }, [queryClient]);

  const refreshPinnedWorkers = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: workersKeys.pinned() });
  }, [queryClient]);

  const toggleWorkerPin = useCallback(
    async (workerId: string) => {
      try {
        if (pinnedWorkers.includes(workerId)) {
          await unpinWorker(workerId);
        } else {
          await pinWorker(workerId);
        }
        await refreshPinnedWorkers();
        await refreshWorkers();
      } catch (err) {
        console.error("Error toggling worker pin:", err);
      }
    },
    [pinnedWorkers, refreshPinnedWorkers, refreshWorkers],
  );

  const setWorkers = useCallback(
    (next: WorkerCard[]) => {
      queryClient.setQueryData(workersKeys.list("summary"), next);
    },
    [queryClient],
  );

  const removeWorker = useCallback(
    (workerId: string) => {
      removeWorkerFromList(queryClient, workerId);
      void refreshPinnedWorkers();
    },
    [queryClient, refreshPinnedWorkers],
  );

  const addWorker = useCallback(
    (worker: WorkerCard) => {
      queryClient.setQueryData<WorkerCard[]>(workersKeys.list("summary"), (prev) =>
        prev ? [...prev, worker] : [worker],
      );
    },
    [queryClient],
  );

  const updateWorker = useCallback(
    (workerId: string, updatedWorker: WorkerCard) => {
      patchWorkerInList(queryClient, { ...updatedWorker, _id: workerId });
    },
    [queryClient],
  );

  const value = useMemo(
    () => ({
      workers,
      loading: listLoading || pinnedLoading,
      error: listError ? "Failed to load worker cards" : null,
      pinnedWorkers,
      setWorkers,
      removeWorker,
      addWorker,
      updateWorker,
      refreshWorkers,
      toggleWorkerPin,
      refreshPinnedWorkers,
    }),
    [
      workers,
      listLoading,
      pinnedLoading,
      listError,
      pinnedWorkers,
      setWorkers,
      removeWorker,
      addWorker,
      updateWorker,
      refreshWorkers,
      toggleWorkerPin,
      refreshPinnedWorkers,
    ],
  );

  return (
    <WorkerListContext.Provider value={value}>
      {children}
    </WorkerListContext.Provider>
  );
}

export function useWorkerList() {
  const context = useContext(WorkerListContext);
  if (context === undefined) {
    throw new Error("useWorkerList must be used within a WorkerListProvider");
  }
  return context;
}
